import { Component, OnInit, Input, OnChanges, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { NgForm } from '@angular/forms';

import { Address } from '../../../models/address';

import { AddressService } from '../../../services/contacts/address.service';
import { NotyService } from '../../../services/common/noty.service';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
})
export class AddressFormComponent implements OnInit, OnChanges {
  /**
   * Contact id for address
   */
  @Input() contactId: number;

  /**
   * Address being created/edited
   */
  @Input() address: Address = new Address();

  /**
   * Emit saved event to parent component
   * @param {EventEmitter<boolean> = new EventEmitter(} ) saved Event emitter to emit saved event to parent component
   */
  @Output() saved: EventEmitter<boolean> = new EventEmitter();

  /**
   * Default form options
   * @type {any}
   */
  formOptions: any = {
    address_types: [],
  };

  /**
   * Indicates if form has been submitted atleast once
   * @type {boolean}
   */
  formSubmitted: boolean = false;

  /**
   * Indicates if form is processing
   * @type {boolean}
   */
  formSubmitting: boolean = false;

  /**
   * Quick search string
   * @type {string}
   */
  quickSearch: string;

  /**
   * Indicates if geocoding is in progress
   * @type {boolean}
   */
  geocoding: boolean = false;

  /**
   * Inject dependencies
   * @param {AddressService} private addressService Service for address resource
   * @param {NotyService}    private notyService    Service for UI notification
   */
  constructor(private addressService: AddressService, private notyService: NotyService) {}

  // Component lifecycle methods
  ngOnInit() {
    this.addressService.formOptions().then((response_json) => {
      this.formOptions = response_json;
      if (this.address.id) {
        for (let addressType of this.formOptions.address_types) addressType.selected = false;
        if (this.address.address_type_ids) for (let addressTypeId of this.address.address_type_ids) for (let addressType of this.formOptions.address_types) if (addressType.value == addressTypeId) addressType.selected = true;
      } else this.address.contact_id = this.contactId;
    });
  }

  /**
   * Called when component variable changes
   * @param {SimpleChanges} changes Object with variables changed in component
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.contact_id && changes.contact_id.currentValue) this.address.contact_id = this.contactId;
  }

  fetchGeo() {
    if (!this.quickSearch || this.quickSearch.trim().length < 5) {
      this.notyService.notify('Please enter an address to search', 'warning', 5000, 'topRight');
      return;
    }

    this.geocoding = true;
    this.addressService
      .geocode(this.contactId, this.quickSearch || '')
      .then((response_json: any) => {
        this.geocoding = false;

        // Handle unexpected response format
        if (!response_json) {
          this.notyService.notify('No response from server', 'error', 5000, 'topRight');
          return;
        }

        // Check if response has the expected structure
        if (typeof response_json.success === 'undefined') {
          // Old response format - just an address object
          if (response_json.city || response_json.street) {
            this.address = response_json;
            this.address.contact_id = this.contactId;
            this.notyService.notify('Address found', 'success', 5000, 'topRight');
            this.addressService.formOptions().then((options) => (this.formOptions = options));
          } else {
            this.notyService.notify('Address not found', 'warning', 5000, 'topRight');
          }
          return;
        }

        // New response format with success/message/address
        if (response_json.success) {
          this.address = response_json.address;
          this.address.contact_id = this.contactId;
          this.notyService.notify(response_json.message || 'Address found', 'success', 5000, 'topRight');
          this.addressService.formOptions().then((options) => (this.formOptions = options));
        } else {
          this.notyService.notify(response_json.message || 'Address not found', 'warning', 5000, 'topRight');
        }
      })
      .catch((error) => {
        this.geocoding = false;
        console.error('Geocoding error:', error);
        this.notyService.notify('Error looking up address. Please try again.', 'error', 5000, 'topRight');
      });
  }

  /**
   * Save Address
   * @param {NgForm} currentForm Angular form
   */
  save(currentForm: NgForm) {
    this.formSubmitted = true;
    if (!currentForm.form.valid) {
      this.notyService.notify('Please check the form again', 'error', 5000, 'topRight');
      return;
    }
    this.address.type_names = [];
    for (let addressType of this.formOptions.address_types) if (addressType.selected) this.address.type_names.push(addressType.label);
    if (this.address.type_names.length < 1) {
      this.notyService.notify('Atleast one type must be selected', 'error', 5000, 'topRight');
      return;
    }
    this.formSubmitting = true;
    this.addressService
      .save(this.address)
      .then((response_json) => {
        this.formSubmitting = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Address saved', 'success', 5000, 'topRight');
          this.saved.emit(true);
          if (!this.address.id) this.address = new Address();
        } else this.notyService.notify(response_json.text || 'Error saving address', 'error', 5000, 'topRight');
      })
      .catch(() => {
        this.formSubmitting = false;
        this.notyService.notify('Error saving address', 'error', 5000, 'topRight');
      });
  }
}
